// ─────────────────────────────────────────────────────────────────
//  PatientInvestigation.jsx  —  v3  "Clinical Clarity" enhanced
//  • Header with PRN chip; stat cards use v3 .pd-stat-card
//  • Visit selector with gradient selected state + glow
//  • Result banner on GRAD_HERO with cyan glow (matches Overview)
//  • Skeleton shimmer loading for the detail panel
//  Fixes vs v2.5 (behavior-preserving):
//    – printedAt now uses dayjs() (was passing a format string as a date)
//    – print <colgroup> now uses valid HTML style="" (was JSX syntax)
//  Props unchanged: { visits, patient, decoded, token, dependentPrn, selfPrn }
// ─────────────────────────────────────────────────────────────────
import React, { useState } from "react";
import {
  Flex, Typography, Spin, Alert, Card, Divider, Button,
} from "antd";
import {
  CalendarOutlined, ExperimentOutlined,
  ArrowRightOutlined, ArrowLeftOutlined,
  CheckCircleOutlined, ClockCircleOutlined,
  ArrowDownOutlined, ArrowUpOutlined,
  MinusOutlined, DownloadOutlined, MailOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  G700, G100, G50,
  GRAY4, GRAY6, GRAY7,
  SLATE, MUTED, WHITE,
  GRAD_HERO, GRAD_BRAND,
  RADIUS_MD, RADIUS_SM, RADIUS_LG, RADIUS_PILL,
  SHADOW_SM, SHADOW_BLUE,
  FF_DISPLAY, FF_BODY, FF_MONO,
  TRANSITION_SM,
} from "./patientTokens";
import { fetchLabResults } from "./Usevisitsummary";

import {
  formatVisitDate,
  visitDateParts,
} from "./dateUtils";

dayjs.extend(relativeTime);
const { Title, Text } = Typography;

// ─── Result status config ──────────────────────────────────────
const RESULT_STATUS = {
  normal:     { label: "Normal",     color: "#16a34a", bg: "#dcfce7", icon: "–"  },
  borderline: { label: "Borderline", color: "#d97706", bg: "#fef3c7", icon: "~"  },
  low:        { label: "Low",        color: "#dc2626", bg: "#fee2e2", icon: "↓"  },
  high:       { label: "High",       color: "#dc2626", bg: "#fee2e2", icon: "↑"  },
  none:       { label: "—",          color: "#9ca3af", bg: "#f3f4f6", icon: null },
};

// React icon versions (for screen only)
const RESULT_STATUS_ICONS = {
  normal:     <MinusOutlined />,
  borderline: <MinusOutlined />,
  low:        <ArrowDownOutlined />,
  high:       <ArrowUpOutlined />,
  none:       null,
};

function getResultStatus(resultNum, range) {
  if (resultNum === null || range.min === null || range.max === null) return "none";
  const span   = range.max - range.min;
  const margin = span * 0.10;
  if (resultNum < range.min) return "low";
  if (resultNum > range.max) return "high";
  if (resultNum <= range.min + margin || resultNum >= range.max - margin) return "borderline";
  return "normal";
}

// ── Screen result cell — colored value ONLY, no pill ──────────
function ResultCell({ value, statusKey }) {
  const s         = RESULT_STATUS[statusKey] || RESULT_STATUS.none;
  const hasStatus = statusKey !== "none";

  return (
    <Text style={{
      fontSize: 13,
      fontWeight: 700,
      color: hasStatus ? s.color : SLATE,
      fontFamily: FF_MONO,
      whiteSpace: "nowrap",
      display: "block",
    }}>
      {value || "—"}
    </Text>
  );
}

// ── Validated badge ────────────────────────────────────────────
function ValidatedBadge({ status }) {
  const isValid = status === "VALIDATE";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 10px", borderRadius: RADIUS_PILL, fontSize: 11, fontWeight: 600,
      background: isValid ? "#dcfce7" : "#fef3c7",
      color:      isValid ? "#16a34a" : "#d97706",
      border:     `1px solid ${isValid ? "#16a34a22" : "#d9770622"}`,
      whiteSpace: "nowrap", flexShrink: 0, fontFamily: FF_BODY,
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: "50%",
        background: isValid ? "#16a34a" : "#d97706",
        display: "inline-block",
      }} />
      {isValid ? "Validated" : "Pending"}
    </span>
  );
}

// ── Helpers ────────────────────────────────────────────────────
const parseRange = (rangeStr) => {
  if (!rangeStr) return { min: null, max: null };
  const clean = rangeStr.replace(/,/g, "");
  const match = clean.match(/([\d.]+)\s*[–\-]\s*([\d.]+)/);
  if (match) return { min: parseFloat(match[1]), max: parseFloat(match[2]) };
  return { min: null, max: null };
};

const parseResultValue = (resStr) => {
  if (!resStr) return null;
  const num = parseFloat(resStr.replace(/,/g, "").trim());
  return isNaN(num) ? null : num;
};

const parseTable = (html) => {
  const parser = new DOMParser();
  const doc    = parser.parseFromString(html, "text/html");
  const table  = doc.querySelector("table");
  if (!table) return null;
  const trs = table.querySelectorAll("tr");
  if (!trs.length) return null;
  let startRow = 0;
  const firstText = trs[0]?.querySelectorAll("td")[0]?.textContent?.trim()?.toLowerCase() || "";
  if (firstText.includes("test") || firstText.includes("description")) startRow = 1;
  const rows = [];
  for (let i = startRow; i < trs.length; i++) {
    const cells = Array.from(trs[i].querySelectorAll("td")).map(c => c.textContent?.trim() || "");
    if (cells.length >= 2) rows.push(cells);
  }
  return rows.length ? rows : null;
};

// ─────────────────────────────────────────────────────────────────
//  buildPrintHTML  —  generates a self-contained HTML string
//  that renders perfectly across Chrome, Edge, Safari, Firefox
// ─────────────────────────────────────────────────────────────────
function buildPrintHTML({ visit, data, patient, decoded }) {
  const prn         = patient?.prn || decoded?.prn || "—";
  const patientName = patient?.full_name || decoded?.fullName || "Patient";
  const visitDate   = formatVisitDate(visit.visited_no, "dddd, MMMM D YYYY");
  const visitNum    = visit.visit_number || "—";
  const printedAt   = dayjs().format("MMM D YYYY, h:mm A");

  const statusColors = {
    normal:     { color: "#166534", bg: "#dcfce7", label: "Normal",     icon: "–"  },
    borderline: { color: "#92400e", bg: "#fef3c7", label: "Borderline", icon: "~"  },
    low:        { color: "#991b1b", bg: "#fee2e2", label: "Low ↓",      icon: "↓"  },
    high:       { color: "#991b1b", bg: "#fee2e2", label: "High ↑",     icon: "↑"  },
    none:       { color: "#6b7280", bg: "#f3f4f6", label: "—",          icon: ""   },
  };

  const getPrintStatus = (resultNum, range) => {
    if (resultNum === null || range.min === null || range.max === null) return "none";
    const span   = range.max - range.min;
    const margin = span * 0.10;
    if (resultNum < range.min) return "low";
    if (resultNum > range.max) return "high";
    if (resultNum <= range.min + margin || resultNum >= range.max - margin) return "borderline";
    return "normal";
  };

  // Build investigation sections
  const investigationSections = data.map((item, itemIdx) => {
    const parsedRows = item.result ? parseTable(item.result) : null;
    const isValidated = item.validated_status === "VALIDATE";
    const orderedAt   = item.ordered_at ? dayjs(item.ordered_at).format("MMM D, YYYY h:mm A") : "—";
    const collectedAt = item.collected_at ? dayjs(item.collected_at).format("MMM D, YYYY h:mm A") : null;

    let tableHTML = "";
    if (parsedRows && parsedRows.length) {
      const rows = parsedRows.map(row => {
        const testName  = row[0] || "";
        const resultVal = row[1] || "";
        const rangeVal  = row.length >= 3 ? row[2] : "";
        const resultNum = parseResultValue(resultVal);
        const range     = parseRange(rangeVal);
        const statusKey = getPrintStatus(resultNum, range);
        const s         = statusColors[statusKey];
        const isAbnormal = statusKey !== "normal" && statusKey !== "none";

        return `
          <tr style="background:${isAbnormal ? s.bg : "transparent"}">
            <td style="padding:9px 12px;border-bottom:1px solid #e5e7eb;font-size:12px;font-weight:500;color:#0f1c2e;">
              ${testName}
            </td>
            <td style="padding:9px 12px;border-bottom:1px solid #e5e7eb;font-size:12px;font-weight:700;color:${s.color};font-family:monospace;white-space:nowrap;">
              ${resultVal || "—"}
            </td>
            <td style="padding:9px 12px;border-bottom:1px solid #e5e7eb;font-size:11px;color:#5e718d;font-family:monospace;">
              ${rangeVal || "—"}
            </td>
            <td style="padding:9px 12px;border-bottom:1px solid #e5e7eb;text-align:center;">
              ${statusKey !== "none" ? `
                <span style="display:inline-block;padding:2px 8px;border-radius:12px;font-size:10px;font-weight:700;background:${s.bg};color:${s.color};">
                  ${s.icon} ${s.label.replace(/ [↓↑]/g, "")}
                </span>
              ` : `<span style="color:#9ca3af;font-size:12px;">—</span>`}
            </td>
          </tr>`;
      }).join("");

      tableHTML = `
        <table style="width:100%;border-collapse:collapse;font-size:12px;margin-top:10px;table-layout:fixed;">
          <colgroup>
            <col style="width:32%" />
            <col style="width:16%" />
            <col style="width:32%" />
            <col style="width:20%" />
          </colgroup>
          <thead>
            <tr style="background:#f0f7ff;">
              <th style="padding:9px 12px;text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:#0f1c2e;border-bottom:2px solid #0e5bc2;">Test</th>
              <th style="padding:9px 12px;text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:#0f1c2e;border-bottom:2px solid #0e5bc2;">Result</th>
              <th style="padding:9px 12px;text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:#0f1c2e;border-bottom:2px solid #0e5bc2;">Reference Range</th>
              <th style="padding:9px 12px;text-align:center;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:#0f1c2e;border-bottom:2px solid #0e5bc2;">Status</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>`;
    } else if (item.result) {
      // Plain text result — strip HTML tags
      const stripped = item.result.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
      tableHTML = `<p style="white-space:pre-wrap;font-size:12px;color:#0f1c2e;line-height:1.6;margin-top:10px;">${stripped}</p>`;
    } else {
      tableHTML = `<p style="font-size:12px;color:#5e718d;margin-top:10px;">No detailed result available.</p>`;
    }

    return `
      <div style="margin-bottom:24px;page-break-inside:avoid;border:1px solid #e3ebf3;border-radius:10px;overflow:hidden;">
        <!-- Card header -->
        <div style="padding:12px 16px;background:#f0f7ff;border-bottom:1px solid #e3ebf3;">
          <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;">
            <div>
              <div style="font-size:14px;font-weight:700;color:#0f1c2e;margin-bottom:4px;">
                ${itemIdx + 1}. ${item.investigation_name || "Investigation"}
              </div>
              <div style="font-size:11px;color:#5e718d;">
                Ordered: ${orderedAt}
                ${collectedAt ? ` &nbsp;·&nbsp; Collected: ${collectedAt}` : ""}
              </div>
            </div>
            <span style="display:inline-flex;align-items:center;gap:4px;padding:3px 10px;border-radius:12px;font-size:10px;font-weight:700;background:${isValidated ? "#dcfce7" : "#fef3c7"};color:${isValidated ? "#166534" : "#92400e"};white-space:nowrap;flex-shrink:0;">
              <span style="width:5px;height:5px;border-radius:50%;background:${isValidated ? "#16a34a" : "#d97706"};display:inline-block;"></span>
              ${isValidated ? "Validated" : "Pending"}
            </span>
          </div>
        </div>
        <!-- Table or text -->
        <div style="padding:0 16px 14px;">
          ${tableHTML}
        </div>
      </div>`;
  }).join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width,initial-scale=1" />
  <title>Lab Results — ${patientName} — ${visitDate}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Inter', sans-serif;
      font-size: 12px;
      color: #0f1c2e;
      background: #fff;
      padding: 32px 40px;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    /* ── Page header ─────────────────────────────────────── */
    .page-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding-bottom: 16px;
      border-bottom: 3px solid #0e5bc2;
      margin-bottom: 20px;
    }
    .hospital-name {
      font-size: 18px;
      font-weight: 800;
      color: #0e5bc2;
      letter-spacing: -0.4px;
    }
    .report-title {
      font-size: 12px;
      font-weight: 600;
      color: #5e718d;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      margin-top: 3px;
    }
    .print-meta {
      font-size: 10px;
      color: #5e718d;
      text-align: right;
    }

    /* ── Patient info strip ──────────────────────────────── */
    .patient-strip {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 0;
      background: #f0f7ff;
      border: 1px solid #dbeeff;
      border-radius: 8px;
      margin-bottom: 20px;
      overflow: hidden;
    }
    .patient-strip-cell {
      padding: 10px 16px;
      border-right: 1px solid #dbeeff;
    }
    .patient-strip-cell:last-child { border-right: none; }
    .psc-label {
      font-size: 9px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      color: #5e718d;
      margin-bottom: 3px;
    }
    .psc-value {
      font-size: 13px;
      font-weight: 700;
      color: #0f1c2e;
    }
    .psc-mono { font-family: 'Courier New', monospace; }

    /* ── Legend ──────────────────────────────────────────── */
    .legend {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
      margin-bottom: 18px;
      padding: 8px 12px;
      background: #f9fafb;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
    }
    .legend-label {
      font-size: 9.5px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.6px;
      color: #5e718d;
      margin-right: 4px;
    }
    .legend-pill {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 10px;
      font-weight: 700;
    }

    /* ── Section heading ─────────────────────────────────── */
    .section-count {
      font-size: 11px;
      font-weight: 600;
      color: #5e718d;
      margin-bottom: 14px;
    }

    /* ── Footer ──────────────────────────────────────────── */
    .page-footer {
      margin-top: 32px;
      padding-top: 12px;
      border-top: 1px solid #e3ebf3;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 9.5px;
      color: #5e718d;
    }
    .footer-sig {
      border-top: 1px solid #0f1c2e;
      padding-top: 6px;
      min-width: 180px;
      font-size: 9.5px;
      color: #0f1c2e;
      text-align: center;
    }

    @media print {
      body { padding: 16px 20px; }
      @page { margin: 12mm 14mm; size: A4; }
    }
  </style>
</head>
<body>

  <!-- Page header -->
  <div class="page-header">
    <div>
      <div class="hospital-name">Atlantis Pediatric Hospital</div>
      <div class="report-title">Laboratory Investigation Report</div>
    </div>
    <div class="print-meta">
      <div>Printed: ${printedAt}</div>
      <div style="margin-top:4px;">Visit: <strong>${visitNum}</strong></div>
    </div>
  </div>

  <!-- Patient info strip -->
  <div class="patient-strip">
    <div class="patient-strip-cell">
      <div class="psc-label">Patient Name</div>
      <div class="psc-value">${patientName}</div>
    </div>
    <div class="patient-strip-cell">
      <div class="psc-label">Patient PRN</div>
      <div class="psc-value psc-mono">${prn}</div>
    </div>
    <div class="patient-strip-cell">
      <div class="psc-label">Visit Date</div>
      <div class="psc-value">${visitDate}</div>
    </div>
  </div>

  <!-- Legend -->
  <div class="legend">
    <span class="legend-label">Key:</span>
    <span class="legend-pill" style="background:#dcfce7;color:#166534;">– Normal</span>
    <span class="legend-pill" style="background:#fef3c7;color:#92400e;">~ Borderline (within 10% of range)</span>
    <span class="legend-pill" style="background:#fee2e2;color:#991b1b;">↓ Low</span>
    <span class="legend-pill" style="background:#fee2e2;color:#991b1b;">↑ High</span>
  </div>

  <!-- Summary count -->
  <div class="section-count">
    ${data.length} investigation${data.length !== 1 ? "s" : ""} for this visit
  </div>

  <!-- Investigation cards -->
  ${investigationSections}

  <!-- Footer -->
  <div class="page-footer">
    <div>
      <div>Atlantis Pediatric Hospital — Confidential Medical Record</div>
      <div style="margin-top:2px;">Generated from Patient Portal · ${printedAt}</div>
    </div>
    <div class="footer-sig">
      <div style="margin-bottom:22px;"></div>
      Authorised Signature
    </div>
  </div>

</body>
</html>`;
}

// ── Visit selector button ──────────────────────────────────────
function VisitItem({ visit, selected, onClick }) {
  const isOpen = visit.status === "OPEN";
  const { dayNum, month } = visitDateParts(visit.visited_no);

  return (
    <button
      onClick={() => onClick(visit)}
      style={{
        display: "block", width: "100%", textAlign: "left",
        background: selected ? GRAD_BRAND : WHITE,
        border: selected ? "1px solid transparent" : `1px solid ${GRAY6}`,
        borderRadius: RADIUS_MD, padding: "10px 12px",
        cursor: "pointer", transition: `all ${TRANSITION_SM}`,
        boxShadow: selected ? SHADOW_BLUE : "none",
        marginBottom: 8, minHeight: 64,
        transform: selected ? "translateX(2px)" : "none",
      }}
      onMouseEnter={e => { if (!selected) { e.currentTarget.style.background = G50; e.currentTarget.style.borderColor = G100; e.currentTarget.style.transform = "translateX(2px)"; } }}
      onMouseLeave={e => { if (!selected) { e.currentTarget.style.background = WHITE; e.currentTarget.style.borderColor = GRAY6; e.currentTarget.style.transform = "none"; } }}
    >
      <Flex align="center" justify="space-between" gap={10}>
        {/* Mini date tile */}
        <div style={{
          width: 40, height: 42, borderRadius: RADIUS_SM, flexShrink: 0,
          background: selected ? "rgba(255,255,255,0.16)" : G50,
          border: selected ? "1px solid rgba(255,255,255,0.24)" : `1px solid ${G100}`,
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
        }}>
          <span style={{
            fontSize: 14, fontWeight: 700, lineHeight: 1,
            color: selected ? WHITE : G700, fontFamily: FF_DISPLAY,
          }}>
            {dayNum}
          </span>
          <span style={{
            fontSize: 7, fontWeight: 700, marginTop: 2,
            textTransform: "uppercase", letterSpacing: "0.5px",
            color: selected ? "rgba(255,255,255,0.7)" : MUTED, fontFamily: FF_BODY,
          }}>
            {month}
          </span>
        </div>

        <div style={{ minWidth: 0, flex: 1 }}>
          <Text strong style={{ color: selected ? WHITE : SLATE, fontSize: 13, display: "block", letterSpacing: "-0.2px", fontFamily: FF_BODY }}>
            {formatVisitDate(visit.visited_no, "MMM D, YYYY")}
          </Text>
          <Text style={{
            color: selected ? "rgba(255,255,255,0.6)" : MUTED,
            fontSize: 10.5, fontFamily: FF_MONO, display: "block",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {visit.visit_number || "—"}
          </Text>
        </div>

        <Flex vertical align="flex-end" gap={4} style={{ flexShrink: 0 }}>
          <span style={{
            fontSize: 9, fontWeight: 700, padding: "2px 8px",
            borderRadius: RADIUS_PILL, lineHeight: 1.8, whiteSpace: "nowrap",
            background: isOpen
              ? (selected ? "rgba(255,255,255,0.2)" : G100)
              : (selected ? "rgba(255,255,255,0.15)" : GRAY7),
            color: isOpen
              ? (selected ? WHITE : G700)
              : (selected ? "rgba(255,255,255,0.7)" : MUTED),
          }}>
            {visit.status}
          </span>
          <ArrowRightOutlined style={{ color: selected ? "rgba(255,255,255,0.5)" : GRAY4, fontSize: 11 }} />
        </Flex>
      </Flex>
    </button>
  );
}

// ── Detail loading skeleton ────────────────────────────────────
const DetailSkeleton = () => (
  <div style={{ padding: "14px 16px" }}>
    <div className="pd-skeleton" style={{ height: 70, borderRadius: RADIUS_MD, marginBottom: 16 }} />
    <div className="pd-skeleton" style={{ height: 32, borderRadius: RADIUS_SM, marginBottom: 16, width: "70%" }} />
    {[0, 1].map(i => (
      <div key={i} className="pd-skeleton" style={{ height: 180, borderRadius: RADIUS_MD, marginBottom: 16 }} />
    ))}
  </div>
);

// ── Lab result detail panel ────────────────────────────────────
function LabResultDetail({ visit, token, patient, decoded, dependentPrn, selfPrn }) {
  const [loading, setLoading] = useState(false);
  const [data,    setData]    = useState([]);
  const [error,   setError]   = useState(null);

  React.useEffect(() => {
    if (!visit) return;
    let cancelled = false;
    setLoading(true); setError(null); setData([]);
    fetchLabResults(visit.visit_number, token, dependentPrn, selfPrn)
      .then(res => { if (!cancelled) { res.success ? setData(res.data) : setError(res.message || "Failed"); } })
      .catch(err => { if (!cancelled) setError(err.message || "Network error"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [visit, token, dependentPrn, selfPrn]);

  // ── Print: open a clean standalone window ──────────────────
  const handleDownloadPDF = () => {
    if (!visit || !data.length) return;
    const html = buildPrintHTML({ visit, data, patient, decoded });
    const win  = window.open("", "_blank", "width=900,height=700");
    if (!win) { alert("Please allow popups for this site to print lab results."); return; }
    win.document.open();
    win.document.write(html);
    win.document.close();
    // Wait for full render before triggering print dialog
    win.onload = () => {
      setTimeout(() => {
        win.focus();
        win.print();
        // Close the helper window after the print dialog is dismissed
        win.onafterprint = () => win.close();
      }, 400);
    };
  };

  // ── Email: plain-text body via mailto ─────────────────────
  const handleSendEmail = () => {
    if (!visit || !data.length) return;
    const prn         = patient?.prn || decoded?.prn || "—";
    const patientName = patient?.full_name || decoded?.fullName || "Patient";
    const visitDate   = dayjs(visit.visited_no).format("MMMM D, YYYY");
    const lines = [
      `Patient: ${patientName}  (PRN: ${prn})`,
      `Visit Date: ${visitDate}`,
      `Visit No.: ${visit.visit_number || "—"}`,
      "",
      "LABORATORY RESULTS",
      "──────────────────",
    ];
    data.forEach((item, idx) => {
      lines.push(`\n${idx + 1}. ${item.investigation_name || "Investigation"} [${item.validated_status === "VALIDATE" ? "Validated" : "Pending"}]`);
      const rows = item.result ? parseTable(item.result) : null;
      if (rows?.length) {
        rows.forEach(r => {
          const range = parseRange(r[2] || "");
          const num   = parseResultValue(r[1] || "");
          const sk    = getResultStatus(num, range);
          const flag  = sk === "low" ? " [↓ LOW]" : sk === "high" ? " [↑ HIGH]" : sk === "borderline" ? " [~ BORDERLINE]" : "";
          lines.push(`   ${r[0] || ""}: ${r[1] || ""}${flag}  (ref: ${r[2] || "—"})`);
        });
      } else if (item.result) {
        lines.push(`   ${item.result.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()}`);
      }
    });
    lines.push("\n\n— Sent from Atlantis Pediatric Patient Portal");
    const subject = `Lab Results — ${patientName} — ${visitDate}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join("\n"))}`;
  };

  // ── States ─────────────────────────────────────────────────
  if (!visit) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 480, padding: "60px 24px", textAlign: "center" }}>
      <div style={{
        width: 64, height: 64, borderRadius: 20,
        background: GRAD_BRAND, boxShadow: SHADOW_BLUE,
        display: "flex", alignItems: "center", justifyContent: "center",
        marginBottom: 16, color: WHITE, fontSize: 28,
      }}>
        <ExperimentOutlined />
      </div>
      <Text strong style={{ color: SLATE, fontSize: 15, display: "block", marginBottom: 6, fontFamily: FF_DISPLAY }}>Select a Visit</Text>
      <Text style={{ color: MUTED, fontSize: 13, maxWidth: 260, fontFamily: FF_BODY }}>Choose a visit from the list to view its lab investigation results.</Text>
    </div>
  );

  if (loading) return <DetailSkeleton />;
  if (error)   return <Alert type="error" message="Error loading investigations" description={error} style={{ margin: 24, borderRadius: RADIUS_SM }} />;
  if (!data.length) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 400, padding: "40px 24px", textAlign: "center" }}>
      <div style={{ width: 54, height: 54, borderRadius: 16, background: GRAY7, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12, fontSize: 22, color: GRAY4 }}>
        <ExperimentOutlined />
      </div>
      <Text strong style={{ color: SLATE, fontSize: 14, display: "block", marginBottom: 4, fontFamily: FF_DISPLAY }}>No results for this visit</Text>
      <Text style={{ color: MUTED, fontSize: 13, fontFamily: FF_BODY }}>Lab results will appear once processed by the laboratory.</Text>
    </div>
  );

  return (
    <div style={{ padding: "14px 16px" }}>
      {/* Visit header banner — deep hero gradient with glow */}
      <div style={{
        position: "relative",
        background: GRAD_HERO,
        borderRadius: RADIUS_MD, padding: "14px 18px", marginBottom: 16,
        overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          background: "radial-gradient(320px 140px at 90% -30%, rgba(34,211,238,0.22), transparent 60%)",
        }} />
        <Flex align="center" justify="space-between" gap={12} style={{ position: "relative" }} wrap="wrap">
          <Flex align="center" gap={12}>
            <div style={{
              width: 42, height: 42, borderRadius: RADIUS_SM,
              background: "rgba(255,255,255,0.14)",
              border: "1px solid rgba(255,255,255,0.22)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: WHITE, fontSize: 18,
            }}>
              <CalendarOutlined />
            </div>
            <div>
              <Text strong style={{ color: WHITE, fontSize: 14, display: "block", fontFamily: FF_BODY }}>
                {formatVisitDate(visit.visited_no)}
              </Text>
              <Text style={{ color: "rgba(255,255,255,0.65)", fontSize: 11, fontFamily: FF_MONO }}>
                {visit.visit_number} · {data.length} investigation{data.length > 1 ? "s" : ""}
              </Text>
            </div>
          </Flex>
          {/* Action buttons */}
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            <Button
              icon={<DownloadOutlined />}
              size="middle"
              onClick={handleDownloadPDF}
              style={{
                background: "rgba(255,255,255,0.12)",
                borderColor: "rgba(255,255,255,0.32)",
                color: WHITE, fontWeight: 600, borderRadius: RADIUS_SM,
                backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
              }}
            >
              <span className="inv-btn-label">PDF</span>
            </Button>
            <Button
              icon={<MailOutlined />}
              size="middle"
              onClick={handleSendEmail}
              style={{
                background: WHITE, border: "none", color: G700,
                fontWeight: 600, borderRadius: RADIUS_SM,
                boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
              }}
            >
              <span className="inv-btn-label">Email</span>
            </Button>
          </div>
        </Flex>
      </div>

      {/* Compact legend */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16, padding: "8px 12px", background: GRAY7, borderRadius: RADIUS_SM, alignItems: "center" }}>
        <Text style={{ color: MUTED, fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", fontFamily: FF_BODY, marginRight: 2, whiteSpace: "nowrap" }}>
          Key:
        </Text>
        {Object.entries(RESULT_STATUS).filter(([k]) => k !== "none").map(([key, s]) => (
          <span key={key} style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "2px 8px", borderRadius: RADIUS_PILL, fontSize: 10.5, fontWeight: 700, background: s.bg, color: s.color, fontFamily: FF_BODY, whiteSpace: "nowrap" }}>
            {RESULT_STATUS_ICONS[key] && <span style={{ fontSize: 8 }}>{RESULT_STATUS_ICONS[key]}</span>}
            {s.label}
          </span>
        ))}
      </div>

      {/* Investigation cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {data.map((item) => {
          const parsedRows = item.result ? parseTable(item.result) : null;
          return (
            <Card key={item.id} bordered={false} style={{ borderRadius: RADIUS_MD, border: `1px solid ${GRAY6}`, overflow: "hidden", boxShadow: SHADOW_SM }} bodyStyle={{ padding: "16px 18px" }}>
              <Flex justify="space-between" align="flex-start" gap={12} style={{ marginBottom: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text strong style={{ fontSize: 15, color: SLATE, fontFamily: FF_DISPLAY, letterSpacing: "-0.2px", display: "block" }}>
                    {item.investigation_name || "Investigation"}
                  </Text>
                  <Flex gap={14} wrap="wrap" style={{ marginTop: 4 }}>
                    <Text style={{ color: MUTED, fontSize: 11.5, fontFamily: FF_BODY }}>
                      <ClockCircleOutlined style={{ marginRight: 4 }} />
                      Ordered: {item.ordered_at ? dayjs(item.ordered_at).format("MMM D, YYYY h:mm A") : "—"}
                    </Text>
                    {item.collected_at && (
                      <Text style={{ color: MUTED, fontSize: 11.5, fontFamily: FF_BODY }}>
                        <CheckCircleOutlined style={{ marginRight: 4 }} />
                        Collected: {dayjs(item.collected_at).format("MMM D, YYYY h:mm A")}
                      </Text>
                    )}
                  </Flex>
                </div>
                <ValidatedBadge status={item.validated_status} />
              </Flex>

              <Divider style={{ margin: "10px 0 14px" }} />

              {parsedRows ? (
                <div className="inv-table-wrap">
                  <table className="inv-table">
                    <colgroup>
                      <col style={{ width: "30%" }} />
                      <col style={{ width: "22%" }} />
                      <col style={{ width: "30%" }} />
                      <col style={{ width: "18%" }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>Test</th>
                        <th>Result</th>
                        <th>Ref. Range</th>
                        <th style={{ textAlign: "center" }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedRows.map((row, idx) => {
                        const testName  = row[0] || "";
                        const resultVal = row[1] || "";
                        const rangeVal  = row.length >= 3 ? row[2] : "";
                        const resultNum = parseResultValue(resultVal);
                        const range     = parseRange(rangeVal);
                        const statusKey = getResultStatus(resultNum, range);
                        const s         = RESULT_STATUS[statusKey];
                        const isAbnormal = statusKey !== "normal" && statusKey !== "none";
                        return (
                          <tr key={idx} style={{ background: isAbnormal ? `${s.bg}50` : "transparent" }}>
                            <td><Text style={{ fontSize: 13, fontWeight: 500, color: SLATE, fontFamily: FF_BODY }}>{testName}</Text></td>
                            <td><ResultCell value={resultVal} statusKey={statusKey} /></td>
                            <td><Text style={{ fontSize: 12, color: MUTED, fontFamily: FF_MONO }}>{rangeVal || "—"}</Text></td>
                            <td style={{ textAlign: "center" }}>
                              {statusKey !== "none" ? (
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "3px 9px", borderRadius: RADIUS_PILL, fontSize: 10.5, fontWeight: 700, background: s.bg, color: s.color, whiteSpace: "nowrap" }}>
                                  {RESULT_STATUS_ICONS[statusKey] && <span style={{ fontSize: 9 }}>{RESULT_STATUS_ICONS[statusKey]}</span>}
                                  {s.label}
                                </span>
                              ) : <span style={{ color: "#9ca3af", fontSize: 13 }}>—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : item.result ? (
                <div style={{ whiteSpace: "pre-wrap", padding: "8px 0" }}>
                  <Text style={{ fontSize: 13.5, color: SLATE, fontFamily: FF_BODY, lineHeight: 1.6 }}>{item.result}</Text>
                </div>
              ) : (
                <Text style={{ color: MUTED, fontSize: 13 }}>No detailed result available.</Text>
              )}
            </Card>
          );
        })}
      </div>

      <style>{`
        .inv-table-wrap { overflow-x:auto;-webkit-overflow-scrolling:touch;border-radius:${RADIUS_SM}px;border:1px solid ${GRAY6}; }
        .inv-table { width:100%;border-collapse:collapse;font-family:${FF_BODY};font-size:13px;min-width:480px;table-layout:fixed; }
        .inv-table thead tr { background:${G50}; }
        .inv-table th { padding:10px 12px;font-weight:600;color:${SLATE};font-size:11px;text-transform:uppercase;letter-spacing:0.4px;border-bottom:2px solid ${G700};white-space:nowrap;text-align:left; }
        .inv-table td { padding:10px 12px;border-bottom:1px solid ${GRAY7};vertical-align:middle;word-break:break-word; }
        .inv-table tr:last-child td { border-bottom:none; }
        .inv-table tbody tr:hover { filter:brightness(0.97); }
        @media (max-width: 480px) {
          .inv-btn-label { display: none; }
          .inv-table th, .inv-table td { padding:8px 8px;font-size:11.5px; }
        }
      `}</style>
    </div>
  );
}

// ── MAIN ──────────────────────────────────────────────────────
function PatientInvestigation({ visits, patient, decoded, token, dependentPrn, selfPrn }) {
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [mobileView,    setMobileView]    = useState("list");

  const viewingDependent = dependentPrn && dependentPrn !== selfPrn;
  const prn = viewingDependent ? dependentPrn : (selfPrn || patient?.prn || decoded?.prn || "");
  const openCount   = visits.filter(v => v.status === "OPEN").length;
  const closedCount = visits.filter(v => v.status !== "OPEN").length;

  const handleSelectVisit = (visit) => { setSelectedVisit(visit); setMobileView("detail"); };

  return (
    <div style={{ width: "100%" }}>
      <style>{`
        .pmr-grid { display:grid;grid-template-columns:280px 1fr;gap:16px;align-items:start; }
        .pmr-back { display:none; }
        @media (max-width:768px) {
          .pmr-grid { display:block; }
          .pmr-panel[data-hidden="true"] { display:none; }
          .pmr-back { display:flex; }
        }
      `}</style>

      {/* Header */}
      <div className="pd-up" style={{ marginBottom: 20 }}>
        <div className="pd-eyebrow"><ExperimentOutlined /> Investigations</div>
        <Title level={3} style={{
          margin: "2px 0 6px", color: SLATE, letterSpacing: "-0.6px",
          fontFamily: FF_DISPLAY, fontWeight: 800,
          fontSize: "clamp(1.3rem, 3vw, 1.75rem)",
        }}>
          Lab Results & Investigations
        </Title>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Text style={{ color: MUTED, fontSize: 13, fontFamily: FF_BODY }}>
            Select a visit to view its lab results
          </Text>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "2px 10px", borderRadius: RADIUS_PILL,
            background: G50, border: `1px solid ${G100}`,
            color: G700, fontSize: 11, fontWeight: 600, fontFamily: FF_MONO,
          }}>
            PRN {prn}
          </span>
        </div>
      </div>

      {/* Stat cards */}
      <Flex className="pd-up pd-up-1" gap={12} wrap="wrap" style={{ marginBottom: 20 }}>
        {[
          { icon: <CalendarOutlined />,    label: "Total",  value: visits.length, accent: G700      },
          { icon: <ClockCircleOutlined />, label: "Open",   value: openCount,     accent: "#f59e0b" },
          { icon: <CheckCircleOutlined />, label: "Closed", value: closedCount,   accent: "#16a34a" },
        ].map(c => (
          <div
            key={c.label}
            className="pd-stat-card"
            style={{
              padding: "13px 18px", display: "flex", alignItems: "center",
              gap: 12, flex: "1 1 120px", "--pd-accent": c.accent,
            }}
          >
            <div style={{
              width: 38, height: 38, borderRadius: 11,
              background: `${c.accent}14`, flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: c.accent, fontSize: 17,
            }}>
              {c.icon}
            </div>
            <div>
              <Text style={{
                color: MUTED, fontSize: 10.5, display: "block", fontFamily: FF_BODY,
                fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px",
              }}>
                {c.label}
              </Text>
              <Text strong className="pd-stat-value" style={{
                color: SLATE, fontSize: 21, lineHeight: 1.1,
                letterSpacing: "-0.5px", fontFamily: FF_DISPLAY,
              }}>
                {c.value}
              </Text>
            </div>
          </div>
        ))}
      </Flex>

      {visits.length === 0 ? (
        <div className="pd-panel" style={{ padding: "56px 24px", textAlign: "center" }}>
          <div style={{ width: 56, height: 56, borderRadius: 18, background: GRAY7, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px", fontSize: 24, color: GRAY4 }}>
            <ExperimentOutlined />
          </div>
          <Text strong style={{ color: SLATE, fontSize: 14, display: "block", marginBottom: 4, fontFamily: FF_DISPLAY }}>
            No visits found
          </Text>
          <Text style={{ color: MUTED, fontSize: 13, fontFamily: FF_BODY }}>
            Lab results will appear here after your first hospital visit.
          </Text>
        </div>
      ) : (
        <>
          {mobileView === "detail" && (
            <button className="pmr-back pd-up" onClick={() => setMobileView("list")} style={{ alignItems: "center", gap: 8, background: WHITE, border: `1px solid ${GRAY6}`, borderRadius: RADIUS_MD, padding: "11px 18px", color: G700, fontWeight: 600, fontSize: 14, boxShadow: SHADOW_SM, width: "100%", marginBottom: 14 }}>
              <ArrowLeftOutlined /> Back to Visit List
            </button>
          )}
          <div className="pmr-grid pd-up pd-up-2">
            {/* Visit list panel */}
            <div className="pmr-panel" data-hidden={mobileView === "detail" ? "true" : "false"}>
              <div className="pd-panel">
                <div style={{ padding: "13px 16px", borderBottom: `1px solid ${GRAY7}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Text strong style={{ color: SLATE, fontSize: 13.5, fontFamily: FF_DISPLAY, letterSpacing: "-0.2px" }}>All Visits</Text>
                  <span style={{
                    fontSize: 10.5, fontWeight: 700, padding: "1px 8px",
                    borderRadius: RADIUS_PILL, background: G50,
                    border: `1px solid ${G100}`, color: G700, fontFamily: FF_MONO,
                  }}>
                    {visits.length}
                  </span>
                </div>
                <div style={{ maxHeight: 580, overflowY: "auto", padding: "12px 10px" }}>
                  {visits.map(v => <VisitItem key={v.id} visit={v} selected={selectedVisit?.id === v.id} onClick={handleSelectVisit} />)}
                </div>
              </div>
            </div>

            {/* Detail panel */}
            <div className="pmr-panel" data-hidden={mobileView === "list" ? "true" : "false"} style={{ marginBottom: 14 }}>
              <div className="pd-panel" style={{ minHeight: 500 }}>
                <LabResultDetail
                  visit={selectedVisit}
                  token={token}
                  patient={patient}
                  decoded={decoded}
                  dependentPrn={viewingDependent ? dependentPrn : null}
                  selfPrn={selfPrn}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default PatientInvestigation;